/**
 * SCRUM-977 — the per-column header dropdown, matching Ryan's `col-menu`
 * (app-shell 7b.html L10947-10996 markup + L7096-7176 styling) EXACTLY as a UI
 * design: a Sort section (two items with a ✓ on the active choice) and, where the
 * column has filter options, a "Filter" heading + option items where an active
 * option reads "Remove filter".
 *
 * Implemented the correct React way on our typed substrate: a Radix Popover
 * (portaled, so the ledger's `overflow-x-auto` + inspector gutter can't clip it),
 * styled with the app's design tokens to match Ryan's look. Opening the menu,
 * choosing a sort, or picking a filter never re-sorts the row or toggles
 * selection (the trigger + content stop propagation).
 */
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { sortLabels, type SortColumn } from './ledger-sort';
import type { SortDirection } from '../campaigns.types';

/** A filter option row: the label shown and a stable key for active-comparison. */
export interface HeaderFilterOption {
  label: string;
  /** Stable identity used to mark the active option (JSON of the filter value). */
  optionKey: string;
}

interface ColumnHeaderMenuProps {
  column: SortColumn;
  label: string;
  align?: 'left' | 'right';
  /** Current sort, so the active direction shows its ✓. */
  sort: { column: SortColumn; direction: SortDirection };
  onSort: (column: SortColumn, direction: SortDirection) => void;
  /** Filter options for this column, or undefined for a sort-only column (e.g. Branch in Ryan). */
  filterOptions?: HeaderFilterOption[];
  /** The optionKey of the currently-active filter for this column, or null. */
  activeOptionKey?: string | null;
  onFilter?: (optionKey: string) => void;
  onClearFilter?: () => void;
}

/** Ryan's check svg (L10956) — a small tick shown on the active sort/filter. */
function CheckMark({ shown }: { shown: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      width={12}
      height={12}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={`h-3 w-3 flex-shrink-0 text-[var(--cyan)] ${shown ? '' : 'invisible'}`}
    >
      <polyline points="2.5 6.5 5 9 9.5 3.5" />
    </svg>
  );
}

/** One menu row — matches Ryan's `.col-menu__item` (0.75rem, active = cyan 600). */
function MenuItem({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`rounded-tk-sm flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[0.75rem] whitespace-nowrap transition-colors hover:bg-[var(--bg-3)] ${
        active ? 'font-semibold text-[var(--cyan)]' : 'text-text-1 font-normal'
      }`}
    >
      <CheckMark shown={active} />
      {children}
    </button>
  );
}

export function ColumnHeaderMenu({
  column,
  label,
  align = 'left',
  sort,
  onSort,
  filterOptions,
  activeOptionKey = null,
  onFilter,
  onClearFilter,
}: ColumnHeaderMenuProps) {
  const labels = sortLabels(column);
  const isSorted = sort.column === column;
  const ascActive = isSorted && sort.direction === 'asc';
  const descActive = isSorted && sort.direction === 'desc';
  const hasFilter = activeOptionKey !== null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Sort and filter ${label}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
          className={`text-text-3 hover:text-text-1 inline-flex items-center gap-1 bg-transparent font-[inherit] tracking-[inherit] text-[inherit] uppercase transition-colors ${
            align === 'right' ? 'flex-row-reverse' : ''
          } ${isSorted || hasFilter ? 'text-text-1' : ''}`}
        >
          <span>{label}</span>
          {/* Sort caret — Ryan shows a triangle on the active column; a faint
              double-caret otherwise so every header reads as interactive. */}
          <span aria-hidden className="text-[0.6em] leading-none">
            {isSorted ? (ascActive ? '▲' : '▼') : '⇅'}
          </span>
          {hasFilter ? (
            <span aria-hidden className="h-1 w-1 rounded-full bg-[var(--cyan)]" />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align === 'right' ? 'end' : 'start'}
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="rounded-tk-md flex min-w-[180px] flex-col gap-px border border-[var(--border-1)] bg-[var(--bg-4)] p-1 text-left tracking-normal normal-case shadow-[var(--shadow-md)]"
      >
        {/* Sort section */}
        <MenuItem
          active={ascActive}
          onClick={() => {
            onSort(column, 'asc');
          }}
        >
          {labels.asc}
        </MenuItem>
        <MenuItem
          active={descActive}
          onClick={() => {
            onSort(column, 'desc');
          }}
        >
          {labels.desc}
        </MenuItem>

        {/* Filter section (Ryan: divider + "Filter" heading + options) */}
        {filterOptions && filterOptions.length > 0 ? (
          <>
            <div className="my-1 h-px bg-[var(--border-0)]" />
            <div className="text-text-3 px-2.5 pt-1.5 pb-0.5 font-mono text-[0.5625rem] font-semibold tracking-[0.06em] uppercase">
              Filter
            </div>
            {filterOptions.map((opt) => {
              const active = activeOptionKey === opt.optionKey;
              return (
                <MenuItem
                  key={opt.optionKey}
                  active={active}
                  onClick={() => {
                    if (active) onClearFilter?.();
                    else onFilter?.(opt.optionKey);
                  }}
                >
                  {active ? 'Remove filter' : opt.label}
                </MenuItem>
              );
            })}
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
